import {
  Box,
  Button,
  MenuItem,
  Modal,
  Table,
  TableBody,
  TableCell as StyledTableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { reduceStockApi } from "../../../../components/api/WarehouseInventoryApiService";
import { getRateForDateApi } from "../../../../components/api/FxRateApiService";

const CURRENCIES = ["USD", "EUR", "SGD", "IDR", "VND", "BRL"];

const FX_RATE_FIELD_BY_CURRENCY = {
  EUR: "usdEur",
  SGD: "usdSgd",
  IDR: "usdIdr",
  VND: "usdVnd",
  BRL: "usdBrl",
};

function runFifoAllocation(totalAmount, candidates) {
  let remaining = Number(totalAmount) || 0;
  const next = {};
  candidates.forEach((candidate) => {
    if (remaining <= 0) {
      next[candidate.shippingInstructionId] = "";
      return;
    }
    const available = Number(candidate.available) || 0;
    const take = Math.min(remaining, available);
    next[candidate.shippingInstructionId] = take > 0 ? String(take) : "";
    remaining -= take;
  });
  return next;
}

const ReduceStockButton = ({ group, onReduced }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [numberOfCartons, setNumberOfCartons] = useState("");
  const [netWeightPerCarton, setNetWeightPerCarton] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [sellingPriceCurrency, setSellingPriceCurrency] = useState("USD");
  const [sellingDate, setSellingDate] = useState("");
  const [allocations, setAllocations] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fxRate, setFxRate] = useState(null);
  const [fxRateLoading, setFxRateLoading] = useState(false);
  const [fxRateError, setFxRateError] = useState("");

  const candidateSis = useMemo(() => {
    const details = Array.isArray(group?.details) ? group.details : [];
    return details
      .filter((d) => d?.type === "Incoming" && Number(d?.storageQuantity) > 0)
      .sort((a, b) => (Number(a?.shippingInstructionId) || 0) - (Number(b?.shippingInstructionId) || 0))
      .map((d) => ({
        shippingInstructionId: d.shippingInstructionId,
        label: `${d.shippingInstructionId ?? ""} - ${d.finalShippingInstructionNumber ?? ""}`,
        available: Number(d.storageQuantity) || 0,
        entryDate: d.entryDate,
      }));
  }, [group]);

  useEffect(() => {
    if (sellingPriceCurrency === "USD" || !sellingDate) {
      setFxRate(null);
      setFxRateError("");
      return;
    }
    let cancelled = false;
    setFxRateLoading(true);
    setFxRateError("");
    getRateForDateApi(sellingDate)
      .then((response) => {
        if (!cancelled) setFxRate(response?.data);
      })
      .catch(() => {
        if (!cancelled) {
          setFxRate(null);
          setFxRateError("Could not fetch exchange rate for this date");
        }
      })
      .finally(() => {
        if (!cancelled) setFxRateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sellingDate, sellingPriceCurrency]);

  const usdRateValue = fxRate ? Number(fxRate[FX_RATE_FIELD_BY_CURRENCY[sellingPriceCurrency]]) : null;
  const sellingPriceUsd =
    sellingPriceCurrency !== "USD" && usdRateValue > 0 && Number(sellingPrice) > 0
      ? Number(sellingPrice) / usdRateValue
      : null;

  const resetState = () => {
    setAmount("");
    setNumberOfCartons("");
    setNetWeightPerCarton("");
    setSellingPrice("");
    setSellingPriceCurrency("USD");
    setSellingDate("");
    setAllocations({});
  };

  const handleOpen = () => {
    const confirmed = window.confirm(
      "Reducing stock is PERMANENT and cannot be undone.\n\n" +
        "It will immediately:\n" +
        "- Decrease the storage quantity on the selected shipping instruction(s)\n" +
        "- Add a new outgoing row to Warehouse Inventory\n" +
        "- Decrease the item's warehouse quantity\n" +
        "- Create a permanent Trade History entry\n\n" +
        "Are you sure you want to continue?",
    );
    if (!confirmed) return;
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetState();
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    setAllocations(runFifoAllocation(value, candidateSis));
  };

  // Default "Amount to Reduce" is cartons * net weight per carton, but the user can still
  // edit the Amount field directly afterward - it's only recomputed when cartons/net weight change.
  const handleCartonsChange = (value) => {
    setNumberOfCartons(value);
    if (Number(value) > 0 && Number(netWeightPerCarton) > 0) {
      handleAmountChange(String(Number(value) * Number(netWeightPerCarton)));
    }
  };

  const handleNetWeightPerCartonChange = (value) => {
    setNetWeightPerCarton(value);
    if (Number(value) > 0 && Number(numberOfCartons) > 0) {
      handleAmountChange(String(Number(numberOfCartons) * Number(value)));
    }
  };

  const handleAllocationChange = (shippingInstructionId, value) => {
    setAllocations((prev) => ({ ...prev, [shippingInstructionId]: value }));
  };

  const totalAllocated = candidateSis.reduce(
    (sum, candidate) => sum + (Number(allocations[candidate.shippingInstructionId]) || 0),
    0,
  );
  const remainingToAllocate = (Number(amount) || 0) - totalAllocated;

  const allAllocationsWithinAvailable = candidateSis.every(
    (candidate) =>
      (Number(allocations[candidate.shippingInstructionId]) || 0) <= candidate.available,
  );

  const canSubmit =
    Number(amount) > 0 &&
    Number(numberOfCartons) > 0 &&
    Number(sellingPrice) > 0 &&
    !!sellingDate &&
    Math.abs(remainingToAllocate) < 0.0001 &&
    allAllocationsWithinAvailable &&
    !submitting;

  const handleSubmit = () => {
    const allocationLines = candidateSis
      .filter((candidate) => Number(allocations[candidate.shippingInstructionId]) > 0)
      .map(
        (candidate) =>
          `  - SI ${candidate.label}: ${Number(allocations[candidate.shippingInstructionId])}`,
      )
      .join("\n");

    const usdLine =
      sellingPriceCurrency !== "USD" && sellingPriceUsd != null
        ? ` (approx ${new Intl.NumberFormat("en-US", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
          }).format(sellingPriceUsd)} USD)`
        : "";

    const finalConfirmed = window.confirm(
      "FINAL CONFIRMATION - this cannot be reversed.\n\n" +
        `Warehouse: ${group?.warehouseId}  |  Item: ${group?.itemId}\n` +
        `Amount to reduce: ${amount}\n` +
        `Number of cartons: ${numberOfCartons}\n` +
        `Selling price: ${sellingPrice} ${sellingPriceCurrency}${usdLine}\n` +
        `Selling date: ${sellingDate}\n\n` +
        "Allocated from:\n" +
        allocationLines +
        "\n\nProceed with this permanent stock reduction?",
    );
    if (!finalConfirmed) return;

    const payload = {
      warehouseId: group?.warehouseId,
      itemId: group?.itemId,
      totalQuantity: Number(amount),
      numberOfCartons: Number(numberOfCartons),
      sellingPrice: Number(sellingPrice),
      sellingPriceCurrency,
      sellingDate,
      allocations: candidateSis
        .filter((candidate) => Number(allocations[candidate.shippingInstructionId]) > 0)
        .map((candidate) => ({
          shippingInstructionId: candidate.shippingInstructionId,
          amount: Number(allocations[candidate.shippingInstructionId]),
        })),
    };

    setSubmitting(true);
    reduceStockApi(payload)
      .then(() => {
        toast.success("Stock reduced successfully");
        setSubmitting(false);
        setOpen(false);
        resetState();
        onReduced && onReduced();
      })
      .catch((error) => {
        setSubmitting(false);
        toast.error(error?.response?.data?.message || "Failed to reduce stock");
      });
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={handleOpen}
        style={{ whiteSpace: "nowrap" }}
      >
        Reduce Stock
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 650,
            maxHeight: "85vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Reduce Stock - Warehouse {group?.warehouseId} / Item {group?.itemId}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 2 }}>
            <TextField
              label="Number of Cartons"
              type="number"
              onWheel={(e) => e.target.blur()}
              value={numberOfCartons}
              onChange={(e) => handleCartonsChange(e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Net Weight per Carton"
              type="number"
              onWheel={(e) => e.target.blur()}
              value={netWeightPerCarton}
              onChange={(e) => handleNetWeightPerCartonChange(e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Amount to Reduce"
              type="number"
              onWheel={(e) => e.target.blur()}
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              sx={{ flex: "1 1 220px" }}
            />
            <TextField
              label="Selling Price (per unit)"
              type="number"
              onWheel={(e) => e.target.blur()}
              value={sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value)}
              sx={{ flex: "1 1 180px" }}
            />
            <TextField
              label="Currency"
              select
              value={sellingPriceCurrency}
              onChange={(e) => setSellingPriceCurrency(e.target.value)}
              sx={{ flex: "1 1 120px" }}
            >
              {CURRENCIES.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Selling Date"
              type="date"
              value={sellingDate}
              onChange={(e) => setSellingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 160px" }}
            />
          </Box>

          {sellingPriceCurrency !== "USD" && (
            <Typography
              variant="body2"
              color={fxRateError ? "error" : "text.secondary"}
              sx={{ marginBottom: 2 }}
            >
              {fxRateLoading
                ? "Fetching exchange rate for selected date..."
                : fxRateError
                ? fxRateError
                : sellingPriceUsd != null
                ? `≈ ${new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  }).format(sellingPriceUsd)} USD (based on selling date rate)`
                : "Enter a selling price and date to see the USD equivalent"}
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Allocate across Shipping Instructions (FIFO suggested, editable)
          </Typography>

          {candidateSis.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
              No incoming shipping instructions with available storage quantity for this item/warehouse.
            </Typography>
          ) : (
            <Table size="small" sx={{ marginBottom: 2 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#20315d" }}>
                  <StyledTableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Shipping Instruction
                  </StyledTableCell>
                  <StyledTableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Available
                  </StyledTableCell>
                  <StyledTableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Allocate
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidateSis.map((candidate) => (
                  <TableRow key={candidate.shippingInstructionId}>
                    <StyledTableCell>{candidate.label}</StyledTableCell>
                    <StyledTableCell>
                      {new Intl.NumberFormat("en-US", {
                        style: "decimal",
                        minimumFractionDigits: 2,
                      }).format(candidate.available)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <TextField
                        type="number"
                        size="small"
                        onWheel={(e) => e.target.blur()}
                        value={allocations[candidate.shippingInstructionId] ?? ""}
                        onChange={(e) =>
                          handleAllocationChange(candidate.shippingInstructionId, e.target.value)
                        }
                        error={
                          (Number(allocations[candidate.shippingInstructionId]) || 0) >
                          candidate.available
                        }
                      />
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography
            variant="body2"
            color={Math.abs(remainingToAllocate) < 0.0001 ? "text.secondary" : "error"}
            sx={{ marginBottom: 2 }}
          >
            Remaining to allocate:{" "}
            {new Intl.NumberFormat("en-US", {
              style: "decimal",
              minimumFractionDigits: 2,
            }).format(remainingToAllocate)}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              Reduce Stock
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ReduceStockButton;
